import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { FormContainer } from 'components/Onboarding/CompanyDetails/styles';
import { Box, Button, EditIcon, Link, TextInput } from '@razorpay/blade/components';
import { useHistory } from 'react-router-dom';
import { routePaths } from 'components/Routes/data';
import { useMutation, useQuery } from 'react-query';
import api from 'api';
import { BvsDataLoadingView } from 'components/Onboarding/components/BvsDataLoadingView';
import { PanNameView } from 'components/Onboarding/components/PanNameView';
import { errorParser } from 'utils/Api';
import { getAppErrorDetails } from 'utils/AppError';
import { useCompanyVerificationData } from '../hooks/useCompanyVerificationDetails';
import { useVerifyCompanyDetailsContext } from './VerifyCompanyDetailsContext';
import { PAN_REGEX } from 'utils/regexes';
import { useDispatch } from 'react-redux';
import { fetchLoggedInUser } from 'reducers/loggedInUser';

const PanOnly = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { isCompanyVerified, authorizedSignatory } = useCompanyVerificationData();
  const { setAlertBannerPropsContext } = useVerifyCompanyDetailsContext();
  const [formData, setFormData] = useState({
    signatoryPan: authorizedSignatory?.pan ?? '',
  });
  const [validationErrors, setValidationErrors] = useState({
    signatoryPan: '',
  });
  const [signatoryNameFromPan, setSignatoryNameFromPan] = useState('');
  const panFieldRef = useRef<HTMLInputElement>(null);

  const signatoryPanQuery = useQuery({
    queryFn: () => api.companyOnboarding.getCompanyNameFromPan(formData.signatoryPan),
    onSuccess: (data) => {
      setSignatoryNameFromPan(data.name ?? '');
    },
    onError: (error) => {
      setValidationErrors((validationErrors) => ({
        ...validationErrors,
        signatoryPan: errorParser(getAppErrorDetails(error))[0],
      }));
    },
    enabled: false,
  });

  useEffect(() => {
    const { signatoryPan } = formData;
    if (signatoryPan && signatoryPan.length >= 10) {
      setSignatoryNameFromPan('');
      signatoryPanQuery.refetch();
    }
  }, [formData.signatoryPan]);

  const companyVerificationByPanMutation = useMutation({
    mutationFn: api.companyOnboarding.verifyCompanyDetailsWithPan,
    onSuccess: (data) => {
      if (data.success) {
        setAlertBannerPropsContext(undefined);
        dispatch(fetchLoggedInUser());
        history.push({
          pathname: routePaths.kyc.companyDetailsVerification.addAddress,
          state: {
            signatoryPan: formData.signatoryPan,
          },
        });
      }
    },
    onError: (error) => {
      setAlertBannerPropsContext({
        title: 'Verification failed',
        color: 'negative',
        description: errorParser(getAppErrorDetails(error))[0],
        isDismissible: false,
      });
    },
  });

  const redirectToDashboard = () => {
    window.location.href = '/dashboard';
  };

  // if the company verification is already completed, redirect back to dashboard
  if (isCompanyVerified) {
    redirectToDashboard();
    return null;
  }

  const handleChange = (e: { name?: string; value?: string }) => {
    if (!e.name) return;

    setValidationErrors((validationErrors) => ({
      ...validationErrors,
      [e.name as string]: '',
    }));

    setFormData({
      ...formData,
      [e.name]: e.value,
    });
  };

  const handleHaveGstinClick = (e: SyntheticEvent<Element, Event>) => {
    e.preventDefault();

    history.push(routePaths.kyc.companyDetailsVerification.gstin);
  };

  const validateFields = () => {
    let result = true;

    if (!new RegExp(PAN_REGEX).test(formData.signatoryPan)) {
      setValidationErrors((validationErrors) => ({
        ...validationErrors,
        signatoryPan: 'Please enter a valid personal PAN',
      }));
      result = false;
    }

    return result;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateFields()) {
      return;
    }

    companyVerificationByPanMutation.mutate({
      signatoryPan: formData.signatoryPan,
      signatoryName: signatoryNameFromPan,
    });
  };

  const handleNotCorrectSignatoryNameClick = () => {
    panFieldRef.current?.focus();
  };

  const isSignatoryNameFetched = signatoryNameFromPan.length > 0;

  return (
    <form onSubmit={handleSubmit}>
      <FormContainer>
        <TextInput
          name="signatoryPan"
          label="Authorised Signatory PAN"
          placeholder="Eg. BZBIJ7819G"
          value={formData.signatoryPan}
          onChange={handleChange}
          isRequired
          ref={panFieldRef}
          autoFocus
          validationState={validationErrors.signatoryPan ? 'error' : 'none'}
          errorText={validationErrors.signatoryPan}
        />
        {isSignatoryNameFetched && (
          <PanNameView title="Authorised Signatory Name" value={signatoryNameFromPan} />
        )}
        {!signatoryPanQuery.isFetching && (
          <Link onClick={handleHaveGstinClick}>Have GSTIN? Verify here→</Link>
        )}
        {signatoryPanQuery.isFetching && (
          <BvsDataLoadingView description="Fetching your details..." />
        )}
      </FormContainer>
      {isSignatoryNameFetched && (
        <Box marginTop="spacing.10" display="flex" flexDirection="column" gap="spacing.5">
          <Box>
            <Button type="submit" isLoading={companyVerificationByPanMutation.isLoading}>
              Confirm & Proceed
            </Button>
          </Box>
          <Box>
            <Button
              variant="secondary"
              iconPosition="right"
              icon={EditIcon}
              onClick={handleNotCorrectSignatoryNameClick}
              isDisabled={companyVerificationByPanMutation.isLoading}>
              Not the correct signatory name? Edit PAN
            </Button>
          </Box>
        </Box>
      )}
    </form>
  );
};

export { PanOnly };
